<script setup lang="ts">
import type { VideoCardFooterProps } from '.';

import { grammarInflection } from '~~/utils';

import VideoProgressBar from './VideoProgressBar.vue';

const props = defineProps<VideoCardFooterProps>();

const emit = defineEmits<{
	seek: [ratio: number];
}>();

const hasFeatures = computed(
	() =>
		props.formattedArea != null ||
		props.roomsInfo != null ||
		props.parkingSpots != null
);

const showVideoProgress = computed(
	() => props.videoProgress !== undefined && props.isVideoPlaying !== undefined
);
</script>

<template>
	<div class="absolute inset-x-0 bottom-0 z-10 flex flex-col gap-3 p-4">
		<div class="flex flex-col gap-1">
			<p
				v-if="formattedPrice"
				class="font-matter-sq text-lg leading-normal tracking-tight text-white drop-shadow-sm md:text-xl"
			>
				{{ formattedPrice }}
			</p>

			<div
				v-if="hasFeatures"
				class="flex items-center gap-2 text-sm font-medium leading-5 text-white"
			>
				<span v-if="formattedArea != null" class="whitespace-nowrap">
					{{ formattedArea }} {{ areaLabel }}
				</span>
				<span
					v-if="formattedArea != null && (roomsInfo || parkingSpots != null)"
					class="size-1 shrink-0 bg-white/20"
				/>
				<span v-if="roomsInfo" class="whitespace-nowrap">
					{{ roomsInfo.count }} {{ roomsInfo.label }}
				</span>
				<span
					v-if="roomsInfo && parkingSpots != null"
					class="size-1 shrink-0 bg-white/20"
				/>
				<span v-if="parkingSpots != null" class="whitespace-nowrap">
					{{ parkingSpots }}
					{{ grammarInflection(parkingSpots, 'vaga', 'vagas') }}
				</span>
			</div>

			<p
				v-if="region || cityState"
				class="truncate text-xs font-medium leading-normal text-white drop-shadow-sm"
			>
				{{ region }}{{ region && cityState ? ', ' : '' }}{{ cityState }}
			</p>
		</div>

		<div
			v-if="showVideoProgress"
			:class="[
				'transition-opacity duration-500 ease-in-out',
				isVideoPlaying ? 'opacity-100' : 'pointer-events-none opacity-0'
			]"
		>
			<VideoProgressBar
				:progress="videoProgress ?? 0"
				@seek="emit('seek', $event)"
			/>
		</div>
	</div>
</template>
